import { createSafeStorage, type Recording } from "@flowr/sdk-core";
import type { RecorderKernelStore } from "@flowr/sdk-recorder-kernel";

type SafeStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;

export const createLocalRecordingStore = (key: string): RecorderKernelStore => {
  const storage: SafeStorage = createSafeStorage();

  const readAll = (): Recording[] => {
    try {
      const raw = storage.getItem(key);
      if (!raw) return [];
      const parsed = JSON.parse(raw) as Recording[];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };

  const writeAll = (rows: Recording[]): void => {
    storage.setItem(key, JSON.stringify(rows));
  };

  return {
    list: async () => readAll().sort((a, b) => (b.updatedAt ?? 0) - (a.updatedAt ?? 0)),
    get: async (id: string) => readAll().find((recording) => recording.id === id) ?? null,
    save: async (recording: Recording) => {
      const rows = readAll();
      const index = rows.findIndex((candidate) => candidate.id === recording.id);
      if (index >= 0) {
        rows[index] = recording;
      } else {
        rows.push(recording);
      }
      writeAll(rows);
      return recording;
    },
    delete: async (id: string) => {
      writeAll(readAll().filter((recording) => recording.id !== id));
    },
    clear: async () => {
      storage.removeItem(key);
    },
  };
};
